import type SiteModel from '../../models/SiteModel';

export const PREVIEW_PROTOCOL_VERSION = 1 as const;

export const PREVIEW_MESSAGE_TYPES = {
  ready: 'renderrush:preview:ready',
  update: 'renderrush:preview:update',
  committed: 'renderrush:preview:committed',
  rejected: 'renderrush:preview:rejected',
} as const;

export const PREVIEW_REJECTION_REASONS = [
  'invalid-model',
  'invalid-identity',
  'render-error',
] as const;

declare const previewSessionIdBrand: unique symbol;
declare const previewRevisionBrand: unique symbol;

export type PreviewSessionId = string & { readonly [previewSessionIdBrand]: true };
export type PreviewRevision = number & { readonly [previewRevisionBrand]: true };
export type PreviewRejectionReason = (typeof PREVIEW_REJECTION_REASONS)[number];

export type PreviewReadyEnvelope = {
  readonly type: typeof PREVIEW_MESSAGE_TYPES.ready;
  readonly version: typeof PREVIEW_PROTOCOL_VERSION;
  readonly sessionId: PreviewSessionId;
};

export type PreviewUpdateEnvelope = {
  readonly type: typeof PREVIEW_MESSAGE_TYPES.update;
  readonly version: typeof PREVIEW_PROTOCOL_VERSION;
  readonly sessionId: PreviewSessionId;
  readonly revision: PreviewRevision;
  readonly siteModel: unknown;
};

export type ValidPreviewUpdateEnvelope = Omit<PreviewUpdateEnvelope, 'siteModel'> & {
  readonly siteModel: SiteModel;
};

export type PreviewCommittedEnvelope = {
  readonly type: typeof PREVIEW_MESSAGE_TYPES.committed;
  readonly version: typeof PREVIEW_PROTOCOL_VERSION;
  readonly sessionId: PreviewSessionId;
  readonly revision: PreviewRevision;
};

export type PreviewRejectedEnvelope = {
  readonly type: typeof PREVIEW_MESSAGE_TYPES.rejected;
  readonly version: typeof PREVIEW_PROTOCOL_VERSION;
  readonly sessionId: PreviewSessionId;
  readonly revision: PreviewRevision;
  readonly reason: PreviewRejectionReason;
};

export type PreviewChildEnvelope =
  | PreviewReadyEnvelope
  | PreviewCommittedEnvelope
  | PreviewRejectedEnvelope;

export type PreviewMessageAddress = {
  readonly source: unknown;
  readonly origin: string;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasExactKeys = (
  value: Readonly<Record<string, unknown>>,
  expectedKeys: readonly string[]
): boolean => {
  const actualKeys = Object.keys(value);
  return (
    actualKeys.length === expectedKeys.length &&
    expectedKeys.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  );
};

const isPreviewSessionId = (value: unknown): value is PreviewSessionId =>
  typeof value === 'string' && UUID_PATTERN.test(value);

const isPreviewRevision = (value: unknown): value is PreviewRevision =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const isPreviewRejectionReason = (value: unknown): value is PreviewRejectionReason =>
  typeof value === 'string' && PREVIEW_REJECTION_REASONS.some((reason) => reason === value);

export function parsePreviewSessionId(value: unknown): PreviewSessionId | null {
  return isPreviewSessionId(value) ? value : null;
}

export function parsePreviewRevision(value: unknown): PreviewRevision | null {
  return isPreviewRevision(value) ? value : null;
}

export function parsePreviewUpdateEnvelope(
  value: unknown,
  expectedSessionId: PreviewSessionId
): PreviewUpdateEnvelope | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['type', 'version', 'sessionId', 'revision', 'siteModel']) ||
    value.type !== PREVIEW_MESSAGE_TYPES.update ||
    value.version !== PREVIEW_PROTOCOL_VERSION ||
    !isPreviewSessionId(value.sessionId) ||
    value.sessionId !== expectedSessionId ||
    !isPreviewRevision(value.revision)
  ) {
    return null;
  }
  return {
    type: PREVIEW_MESSAGE_TYPES.update,
    version: PREVIEW_PROTOCOL_VERSION,
    sessionId: value.sessionId,
    revision: value.revision,
    siteModel: value.siteModel,
  };
}

export function parsePreviewChildEnvelope(
  value: unknown,
  expectedSessionId: PreviewSessionId
): PreviewChildEnvelope | null {
  if (
    !isRecord(value) ||
    value.version !== PREVIEW_PROTOCOL_VERSION ||
    !isPreviewSessionId(value.sessionId) ||
    value.sessionId !== expectedSessionId
  ) {
    return null;
  }

  switch (value.type) {
    case PREVIEW_MESSAGE_TYPES.ready:
      return hasExactKeys(value, ['type', 'version', 'sessionId'])
        ? {
            type: PREVIEW_MESSAGE_TYPES.ready,
            version: PREVIEW_PROTOCOL_VERSION,
            sessionId: value.sessionId,
          }
        : null;
    case PREVIEW_MESSAGE_TYPES.committed:
      return hasExactKeys(value, ['type', 'version', 'sessionId', 'revision']) &&
        isPreviewRevision(value.revision)
        ? {
            type: PREVIEW_MESSAGE_TYPES.committed,
            version: PREVIEW_PROTOCOL_VERSION,
            sessionId: value.sessionId,
            revision: value.revision,
          }
        : null;
    case PREVIEW_MESSAGE_TYPES.rejected:
      return hasExactKeys(value, ['type', 'version', 'sessionId', 'revision', 'reason']) &&
        isPreviewRevision(value.revision) &&
        isPreviewRejectionReason(value.reason)
        ? {
            type: PREVIEW_MESSAGE_TYPES.rejected,
            version: PREVIEW_PROTOCOL_VERSION,
            sessionId: value.sessionId,
            revision: value.revision,
            reason: value.reason,
          }
        : null;
    default:
      return null;
  }
}

export function createPreviewUpdateEnvelope(
  sessionId: PreviewSessionId,
  revision: PreviewRevision,
  siteModel: SiteModel
): ValidPreviewUpdateEnvelope {
  return {
    type: PREVIEW_MESSAGE_TYPES.update,
    version: PREVIEW_PROTOCOL_VERSION,
    sessionId,
    revision,
    siteModel,
  };
}

export function isExpectedPreviewMessageAddress(
  actual: PreviewMessageAddress,
  expected: PreviewMessageAddress
): boolean {
  return actual.source === expected.source && actual.origin === expected.origin;
}

export function buildPreviewRuntimeUrl(params: {
  altId: string;
  sessionId: PreviewSessionId;
  currentHref: string;
}): string {
  const url = new URL(params.currentHref);
  const basePath = url.pathname.replace(/\/+$/, '');
  url.pathname = `${basePath}/preview/runtime/${encodeURIComponent(params.altId)}`;
  url.search = new URLSearchParams({ sessionId: params.sessionId }).toString();
  url.hash = '';
  return url.toString();
}
